"use client";
import { useEffect, useRef } from "react";
import { useReplayReadiness } from "./playback/use-replay-readiness";
import type { ReplayTimingController } from "./playback/use-replay-timing";
import {
  type CoworldReplayFrameProps,
  type ReplaySession,
} from "./replay-frame.types";
import { ReplayFailure } from "./replay-failure";
import { ReplayStarting } from "./replay-starting";
import { ReadyReplayFrame } from "./ready-replay-frame";

export function CoworldReplayRuntime({
  session,
  sessionError,
  slowStart,
  letterboxBg,
  telemetry,
  onRetry,
  onPause,
  ...props
}: CoworldReplayFrameProps & {
  session: ReplaySession | null;
  sessionError: string | null;
  slowStart: boolean;
  letterboxBg: string | null;
  telemetry: ReplayTimingController;
  onRetry: () => void;
  onPause: () => void;
}) {
  const playback = useReplayReadiness({ ...props, telemetry });
  const error = sessionError ?? playback.error;
  const onStateChange = useRef(props.onStateChange);
  useEffect(() => {
    onStateChange.current = props.onStateChange;
  }, [props.onStateChange]);
  const state = error ? "error" : playback.painted ? "ready" : "starting";
  useEffect(() => {
    onStateChange.current?.(state);
  }, [state]);

  if (error)
    return <ReplayFailure {...props} error={error} onRetry={onRetry} />;

  if (!session) return <ReplayStarting {...props} slowStart={slowStart} />;

  return (
    <ReadyReplayFrame
      {...props}
      session={session}
      playback={playback}
      badge={telemetry.badge}
      letterboxBg={letterboxBg}
      onPause={onPause}
    />
  );
}
